// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

const AVATAR_SIZE = 120;

function hashSeed(seed: string): number {
  let hash = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function createRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
    return state / 4294967295;
  };
}

function hsl(hue: number, saturation: number, lightness: number): string {
  return `hsl(${Math.round(hue)}, ${Math.round(saturation)}%, ${Math.round(lightness)}%)`;
}

export function createAvatarSvg(seed: string): string {
  const normalizedSeed = seed.trim() || 'player';
  const seedHash = hashSeed(normalizedSeed);
  const random = createRandom(seedHash);
  const hue = seedHash % 360;
  const gradientId = `avatar-bg-${seedHash.toString(16)}`;

  const skin = hsl((hue + 24) % 360, 38, 76);
  const shirt = hsl((hue + 190) % 360, 56, 43);
  const hair = hsl((hue + 310) % 360, 20 + random() * 32, 18 + random() * 24);
  const accentA = hsl(hue, 65, 56);
  const accentB = hsl((hue + 55) % 360, 64, 40);

  const faceCx = AVATAR_SIZE * 0.5 + (random() - 0.5) * 3;
  const faceCy = AVATAR_SIZE * 0.5 + (random() - 0.5) * 3;
  const faceRadius = AVATAR_SIZE * (0.23 + random() * 0.03);
  const eyeOffsetX = 10 + random() * 4;
  const eyeY = faceCy - 2 + random() * 4;
  const eyeRadius = 2 + random();
  const mouthY = faceCy + 12 + random() * 6;
  const mouthLeftX = faceCx - (11 + random() * 4);
  const mouthRightX = faceCx + (11 + random() * 4);
  const mouthCurve = mouthY + (random() - 0.5) * 7;
  const hairHeight = AVATAR_SIZE * (0.17 + random() * 0.06);
  const shoulderTop = AVATAR_SIZE * (0.72 + random() * 0.04);

  return [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${AVATAR_SIZE} ${AVATAR_SIZE}" role="img" aria-label="avatar">`,
    '<defs>',
    `<linearGradient id="${gradientId}" x1="0%" y1="0%" x2="100%" y2="100%">`,
    `<stop offset="0%" stop-color="${accentA}" />`,
    `<stop offset="100%" stop-color="${accentB}" />`,
    '</linearGradient>',
    '</defs>',
    `<rect width="${AVATAR_SIZE}" height="${AVATAR_SIZE}" rx="22" fill="url(#${gradientId})" />`,
    `<circle cx="${faceCx}" cy="${faceCy}" r="${faceRadius}" fill="${skin}" />`,
    `<path d="M${faceCx - faceRadius - 4} ${faceCy - 2} Q${faceCx} ${faceCy - hairHeight} ${faceCx + faceRadius + 4} ${faceCy - 2} L${faceCx + faceRadius + 4} ${faceCy - 14} Q${faceCx} ${faceCy - hairHeight - 12} ${faceCx - faceRadius - 4} ${faceCy - 14} Z" fill="${hair}" />`,
    `<circle cx="${faceCx - eyeOffsetX}" cy="${eyeY}" r="${eyeRadius}" fill="#0f172a" />`,
    `<circle cx="${faceCx + eyeOffsetX}" cy="${eyeY}" r="${eyeRadius}" fill="#0f172a" />`,
    `<path d="M${mouthLeftX} ${mouthY} Q${faceCx} ${mouthCurve} ${mouthRightX} ${mouthY}" stroke="#0f172a" stroke-width="2.6" stroke-linecap="round" fill="none" />`,
    `<path d="M22 ${AVATAR_SIZE} Q${faceCx} ${shoulderTop} 98 ${AVATAR_SIZE} Z" fill="${shirt}" />`,
    '</svg>',
  ].join('');
}
